import platform from 'platform';
import {Injectable} from '@angular/core';
import {BehaviorSubject, Observable, of} from 'rxjs';
import {countryConfig} from 'src/country-config/country-config';
import {catchError, switchMap, take, tap} from 'rxjs/operators';
import {BatchService} from '../../networking/services/batch/batch.service';
import {LogEvent} from '../../networking/models/batch/request/batch-req';
import {LocalStorageManager} from '../local-storage/local-storage.manager';
import {SessionStorageManager} from '../session-storage/session-storage.manager';
import {IpService} from '../../networking/services/customer/ip.service';
import {BatchResponse} from '../../networking/models/batch/response/batch-res';
import {LocalCustomer} from '../../networking/models/customer/customer';
import {LocalCity} from '../../networking/models/city/city';
import {PrimeSubscription} from '../../networking/models/prime/prime';

declare const window: any;

@Injectable({
  providedIn: 'root',
})
export class BatchManager {
  private ipAddressSubject = new BehaviorSubject<string>('-');

  constructor(
    private batchService: BatchService,
    private localStorageManager: LocalStorageManager,
    private sessionStorageManager: SessionStorageManager,
    private ipService: IpService,
  ) {}

  private loadIpAddress(): void {
    this.ipService
      .getIpAddress()
      .pipe(
        tap(response => {
          this.ipAddressSubject.next(response.ip);
        }),
        catchError(() => {
          this.ipAddressSubject.next('-');
          return of(null);
        }),
      ).subscribe();
  }

  get customer(): LocalCustomer {
    return this.localStorageManager.get('customer');
  }

  get city(): LocalCity {
    return this.sessionStorageManager.get('city');
  }

  get primeSubscription(): PrimeSubscription {
    return this.sessionStorageManager.get('primeSubscription');
  }

  get date(): string {
    return new Date().toLocaleString('en-US', {
      timeZone: 'America/Bogota',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
    });
  }

  get deviceId(): string {
    return window.deviceId || '-';
  }

  get sessionId(): number | string {
    return window.sessionId || '-';
  }

  get navigator(): string {
    const userAgent = window.navigator.userAgent || '-';
    const device = userAgent.match(/\(([^)]{1,200})\)/);

    return device ? device[1] : '';
  }

  get documentNumber(): string {
    if (!this.customer || !this.customer.documentNumber) {
      return '-';
    }

    return btoa(this.customer.documentNumber.toString());
  }

  logEvent(event: string, data: any): Observable<BatchResponse> {
    if (!this.ipAddressSubject.getValue() || this.ipAddressSubject.getValue() === '-') {
      this.loadIpAddress();
    }

    return this.ipAddressSubject.pipe(
      take(1),
      switchMap(ipAddress => {
        const logEventData: LogEvent = {
          event_name: event,
          event_source: countryConfig.amplitudeConfig.platform,
          event_time: new Date().getTime(),
          external_id: this.customer?.analyticsUUID || '-',
          data: {
            user_properties: {
              user_id: this.customer?.analyticsUUID || '-',
              device_id: this.deviceId,
              platform: countryConfig.amplitudeConfig.platform,
              version: countryConfig.amplitudeConfig.version,
              library: countryConfig.amplitudeConfig.library,
              country: countryConfig.countryName,
              carrier: '-',
              initial_referring_domain: countryConfig.amplitudeConfig.initialReferringDomain,
              initial_referrer: countryConfig.amplitudeConfig.initialReferrer,
              city: this.city?.municipalList?.[0]?.name,
              device: this.navigator,
              dma: '-',
              id: '-',
              language: countryConfig.amplitudeConfig.language,
              latitude: this.city?.municipalList?.[0]?.latitude,
              longitude: this.city?.municipalList?.[0]?.longitude,
              os: platform.name,
              region: '-',
              start_version: countryConfig.amplitudeConfig.startVersion,
              city_code: this.city?.id,
              country_code: this.customer?.country || 'CO',
              email: this.customer?.email || '-',
              first_name: this.customer?.firstName || '-',
              gender: this.customer?.gender || '-',
              last_name: this.customer?.lastName || '-',
              document_number: this.documentNumber,
              phone: this.customer?.phone || '-',
              prime: this.primeSubscription?.active || false,
              vip: this.customer?.vip || false,
              client_event_time: this.date,
              client_upload_time: this.date,
              server_upload_time: this.date,
              server_received_time: this.date,
              ip_address: ipAddress,
              session_id: this.sessionId,
            },
            event_properties: {
              ...data,
            },
          },
        };

        return this.batchService.logEvent(logEventData);
      }),
    );
  }
}
